import { useState } from "react";
import { useLang } from "../i18n";
import { useBoot } from "../components/Layout";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { Turnstile } from "../components/Turnstile";
import { Field, Input, Textarea, Select, Consent, Honeypot, SubmitButton, FormStatus } from "../components/forms";
import { api, apiError } from "../lib/api";
import { MapPin, Phone, Mail, Clock, LifeBuoy } from "lucide-react";

const Contact = () => {
  const { lang, t, p } = useLang();
  const boot = useBoot();
  const contact = boot?.settings?.contact || {};
  const hours = boot?.settings?.office_hours || {};
  const support = boot?.settings?.support || {};
  const locations = boot?.locations || [];

  const [form, setForm] = useState({ department: "general", name: "", email: "", phone: "", subject: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [token, setToken] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState({ ok: false, msg: "" });
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.name.trim().length < 2) errs.name = t.forms.required;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) errs.email = t.forms.invalidEmail;
    if (form.message.trim().length < 5) errs.message = t.forms.required;
    if (!consent) errs.consent = t.forms.consentRequired;
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true); setStatus({ ok: false, msg: "" });
    try {
      const res = await api.post("/public/contact", { ...form, locale: lang, consent, turnstile_token: token, honeypot });
      setStatus({ ok: true, msg: res.data.message || t.contact.successMsg });
      setForm({ department: "general", name: "", email: "", phone: "", subject: "", message: "" });
      setConsent(false);
    } catch (err) { setStatus({ ok: false, msg: apiError(err, t.forms.error) }); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="contact-page">
      <Seo title={t.nav.contact} path="contact" />
      <PageHero eyebrow="BULA TELECOMS SA" title={t.contact.title} sub={t.contact.subtitle} crumbs={[{ label: t.nav.contact }]} />

      <section className="bg-[#F5F5F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-6">
            {locations.map((loc) => (
              <Reveal key={loc.id} className="bg-white border border-[#101214]/10 rounded-2xl p-7">
                <h2 className="font-bold text-[#101214] flex items-center gap-2">
                  <MapPin size={17} className="text-[#FF6600]" />{loc.name}
                </h2>
                <p className="mt-3 text-sm text-[#101214]/65 whitespace-pre-line leading-relaxed">
                  {lang === "fr" ? loc.data?.address_fr : loc.data?.address_en}
                </p>
                {loc.data?.phone && (
                  <a href={`tel:${loc.data.phone.replace(/\s/g, "")}`} data-testid={`contact-phone-${loc.id}`}
                    className="mt-3 flex items-center gap-2 text-sm font-bold text-[#FF6600]">
                    <Phone size={14} />{loc.data.phone}
                  </a>
                )}
                <a href={`mailto:${loc.data?.email || "info@bulatelecoms.com"}`} data-testid={`contact-email-${loc.id}`}
                  className="mt-1.5 flex items-center gap-2 text-sm text-[#101214]/65 hover:text-[#FF6600]">
                  <Mail size={14} className="text-[#FF6600]" />{loc.data?.email || "info@bulatelecoms.com"}
                </a>
              </Reveal>
            ))}
            <Reveal className="bg-[#101214] rounded-2xl p-7">
              <h2 className="font-bold text-white flex items-center gap-2"><Clock size={17} className="text-[#FF6600]" />{t.contact.hours}</h2>
              <p className="mt-3 text-sm text-white/65">{(lang === "fr" ? hours.fr : hours.en) || ""}</p>
              {support?.enabled && (support.phone || support.portal_url) && (
                <div className="mt-5 pt-5 border-t border-white/10" data-testid="network-support">
                  <h3 className="font-bold text-[#FFC000] text-sm flex items-center gap-2"><LifeBuoy size={15} />{t.contact.emergency}</h3>
                  {support.phone && <a href={`tel:${support.phone.replace(/\s/g, "")}`} className="mt-2 block text-sm font-bold text-[#FF9900]">{support.phone}</a>}
                  {support.portal_url && <a href={support.portal_url} target="_blank" rel="noopener noreferrer" className="mt-1 block text-sm text-white/70 underline">{support.portal_url}</a>}
                </div>
              )}
            </Reveal>
            <Reveal className="rounded-2xl overflow-hidden border border-[#101214]/10">
              <iframe title="Bula Telecoms — Kinshasa" data-testid="contact-map"
                src="https://www.openstreetmap.org/export/embed.html?bbox=15.28%2C-4.35%2C15.34%2C-4.30&layer=mapnik&marker=-4.3219%2C15.3119"
                className="w-full h-64 border-0" loading="lazy" />
            </Reveal>
          </div>

          <Reveal className="lg:col-span-7">
            <form onSubmit={submit} className="bg-white border border-[#101214]/10 rounded-2xl p-7 sm:p-9 space-y-5" data-testid="contact-form">
              <Field label={t.contact.fields.department} htmlFor="ct-dept">
                <Select id="ct-dept" data-testid="contact-department-select" value={form.department} onChange={set("department")}>
                  {Object.entries(t.contact.departments).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </Select>
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label={t.contact.fields.name} required error={errors.name} htmlFor="ct-name">
                  <Input id="ct-name" data-testid="contact-name-input" value={form.name} onChange={set("name")} />
                </Field>
                <Field label={t.contact.fields.email} required error={errors.email} htmlFor="ct-email">
                  <Input id="ct-email" type="email" data-testid="contact-email-input" value={form.email} onChange={set("email")} />
                </Field>
                <Field label={t.contact.fields.phone} htmlFor="ct-phone">
                  <Input id="ct-phone" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} />
                </Field>
                <Field label={t.contact.fields.subject} htmlFor="ct-subject">
                  <Input id="ct-subject" data-testid="contact-subject-input" value={form.subject} onChange={set("subject")} />
                </Field>
              </div>
              <Field label={t.contact.fields.message} required error={errors.message} htmlFor="ct-message">
                <Textarea id="ct-message" rows={6} data-testid="contact-message-input" value={form.message} onChange={set("message")} />
              </Field>
              <Consent checked={consent} onChange={setConsent} error={errors.consent} testid="contact-consent" />
              <Turnstile action="contact" onToken={setToken} />
              <p className="text-xs text-[#101214]/45">{t.forms.privacyNote}</p>
              <FormStatus ok={status.ok} msg={status.msg} testid="contact-form-status" />
              <SubmitButton busy={busy} testid="contact-submit-button">{t.contact.submit}</SubmitButton>
              <Honeypot value={honeypot} onChange={setHoneypot} />
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
